import type { ApplicationConfig } from './ApplicationConfig';
import { Stage } from './ApplicationConfig';
import { devConfig } from './ApplicationConfig.dev';
import { stagingConfig } from './ApplicationConfig.staging';
import { prodConfig } from './ApplicationConfig.prod';
import { localConfig } from './ApplicationConfig.local';
import { resolveBackendStage, resolveStage } from './stageOverride';

const CONFIG_BY_STAGE: Record<Stage, ApplicationConfig> = {
  [Stage.PROD]: prodConfig,
  [Stage.STAGING]: stagingConfig,
  [Stage.DEV]: devConfig,
  [Stage.LOCAL]: localConfig,
};

/** Stamped by vite from `STAGE`; declared as a global so tests never touch `import.meta`. */
declare const __STAGE_OVERRIDE__: string | undefined;

const readStageOverride = (): string | undefined =>
  typeof __STAGE_OVERRIDE__ === 'string' ? __STAGE_OVERRIDE__ : undefined;

export class ConfigReader {
  private static instance: ConfigReader;
  private config: ApplicationConfig;

  private constructor() {
    this.config = this.detectEnvironment();
  }

  public static getInstance(): ConfigReader {
    if (!ConfigReader.instance) {
      ConfigReader.instance = new ConfigReader();
    }
    return ConfigReader.instance;
  }

  public getConfig(): ApplicationConfig {
    return this.config;
  }

  /**
   * The page keeps its own stage — which is what decides sign-in — and only borrows another
   * environment's API addresses, so `STAGE=dev npm run dev` reads dev data without a dev login.
   */
  private detectEnvironment(): ApplicationConfig {
    const hostname = window.location.hostname;
    const base = CONFIG_BY_STAGE[resolveStage(hostname)];
    const backend = CONFIG_BY_STAGE[resolveBackendStage(hostname, readStageOverride())];
    if (backend === base) {
      return base;
    }
    return {
      ...base,
      apiUrl: backend.apiUrl,
      internalApiUrl: backend.internalApiUrl,
      csAgentApiUrl: backend.csAgentApiUrl,
      apiKey: backend.apiKey,
    };
  }
}
